/**
 * @created     2026-03-19
 * @description Overlay modal for personal data changes (address, name, bank details).
 *              These are NOT handled in the bot chat for privacy/data protection reasons.
 *              Depends on: ChatAction modalId 'stammdaten' from bot response
 */
import { TrackingService } from '../../../core/services/tracking.service';
import {
  Component,
  EventEmitter,
  Output,
  input,
  ChangeDetectionStrategy,
  signal,
  isDevMode,
  computed,
  inject,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { isValidIban, normalizeIban } from '../../utils/form-validators';

export interface StammdatenChangePayload {
  confirmed_correct: boolean;
  selected_fields: string[];
  new_stammdaten: {
    full_name?: string;
    address?: string;
    iban?: string;
    email?: string;
    telefon?: string;
  };
}

@Component({
  selector: 'app-stammdaten-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './stammdaten-modal.component.html',
  styleUrl: './stammdaten-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StammdatenModalComponent {
  readonly currentData = input<{ sd_name?: unknown; sd_strasse?: unknown; sd_plz?: unknown; sd_ort?: unknown } | null>(null);
  readonly currentName = computed(() => this.text(this.currentData()?.sd_name));
  readonly currentAddress = computed(() => [
    this.text(this.currentData()?.sd_strasse),
    [this.text(this.currentData()?.sd_plz), this.text(this.currentData()?.sd_ort)].filter(Boolean).join(' '),
  ].filter(Boolean).join(', '));
  readonly confirmedCorrect = signal(false);
  /** Emits when the modal should be closed */
  @Output() close = new EventEmitter<void>();

  /** Emits the selected master-data changes on submit */
  @Output() submitStammdaten = new EventEmitter<StammdatenChangePayload>();

  readonly showDevHelper = isDevMode();
  readonly changeName = signal(false);
  readonly changeAddress = signal(false);
  readonly changeIban = signal(false);
  readonly fullName = signal('');
  readonly address = signal('');
  readonly iban = signal('');
  readonly email = signal('');
  readonly telefon = signal('');
  nameEdited = false;
  addressEdited = false;
  readonly submitAttempted = signal(false);

  readonly ibanFormatted = computed(() => this.formatIban(this.iban()));
  readonly ibanValid = computed(() => !this.changeIban() || isValidIban(this.iban()));
  readonly isValid = computed(() => {
    const hasOptionalContactChange = this.email().trim().length > 0 || this.telefon().trim().length > 0;
    const hasSelectedChange = this.changeName() || this.changeAddress() || this.changeIban() || hasOptionalContactChange;
    const fullNameOk = !this.changeName() || this.fullName().trim().length >= 2;
    const addressOk = !this.changeAddress() || this.address().trim().length >= 5;

    return this.confirmedCorrect() && hasSelectedChange && fullNameOk && addressOk && this.ibanValid();
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['currentData']) return;
    if (!this.nameEdited) this.fullName.set(this.currentName());
    if (!this.addressEdited) this.address.set(this.currentAddress());
  }

  onClose(): void {
    this.confirmedCorrect.set(false);
    this.close.emit();
  }

  insertTestData(): void {
    if (!this.showDevHelper) return;
    this.nameEdited = true;
    this.addressEdited = true;
    this.changeName.set(true);
    this.changeAddress.set(true);
    this.changeIban.set(true);
    this.fullName.set('TESTDATEN Erika Beispiel');
    this.address.set('Beispielweg 12, 12345 Musterstadt');
    this.iban.set('DE89370400440532013000');
    this.email.set('testdaten.stammdaten@example.invalid');
    this.telefon.set('+49 151 23456789');
  }

  onIbanInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = normalizeIban(input.value).slice(0, 34);
    this.iban.set(raw);
    input.value = this.formatIban(raw);
  }

  private readonly tracking = inject(TrackingService);

  onSubmit(): void {
    this.submitAttempted.set(true);
    if (!this.isValid()) {
      this.tracking.error('validation', 'master_data');
      return;
    }

    const selectedFields: string[] = [];
    const newStammdaten: StammdatenChangePayload['new_stammdaten'] = {};

    if (this.changeName()) {
      selectedFields.push('full_name');
      newStammdaten.full_name = this.fullName().trim();
    }
    if (this.changeAddress()) {
      selectedFields.push('address');
      newStammdaten.address = this.address().trim();
    }
    if (this.changeIban()) {
      selectedFields.push('iban');
      newStammdaten.iban = normalizeIban(this.iban());
    }
    if (this.email().trim() !== '') {
      selectedFields.push('email');
      newStammdaten.email = this.email().trim();
    }
    if (this.telefon().trim() !== '') {
      selectedFields.push('telefon');
      newStammdaten.telefon = this.telefon().trim();
    }

    this.submitStammdaten.emit({
      confirmed_correct: true,
      selected_fields: selectedFields,
      new_stammdaten: newStammdaten,
    });
  }

  private formatIban(raw: string): string {
    const clean = raw.replace(/[\s-]/g, '').toUpperCase().slice(0, 34);
    return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
  }

  private text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

}
